import { ref, computed } from 'vue';

export interface MapContextData {
    area: {
        type: 'rectangle' | 'polygon' | 'circle';
        bounds: [[number, number], [number, number]];
        centerCoordinates: [number, number];
        areaKm2: number;
    };
    chargingStations: {
        total: number;
        byPower: Record<string, number>;
        byStatus: Record<string, number>;
        stations: Array<{
            id: string;
            name: string;
            lat: number;
            lng: number;
            power: string;
            status: string;
        }>;
    };
    traffic?: {
        level: 'low' | 'moderate' | 'high' | 'congestion' | 'unknown';
        avgSpeed?: number;
    };
    weather?: {
        current: { temp: number; precipitation: number; description: string };
        forecast: Array<{ hour: string; temp: number; rain: number }>;
    };
    timestamp: number;
}

const mapContext = ref<MapContextData | null>(null);

export function useMapContext() {
    const hasContext = computed(() => mapContext.value !== null);

    const contextSummary = computed(() => {
        if (!mapContext.value) return '';

        const ctx = mapContext.value;
        let summary = `📍 Area selezionata: ${ctx.area.areaKm2.toFixed(2)} km²\n\n`;

        // Charging stations
        if (ctx.chargingStations.total > 0) {
            summary += `🔌 **Colonnine ricarica**: ${ctx.chargingStations.total}\n`;
            const operational = ctx.chargingStations.byStatus['Operational'] || 0;
            if (operational > 0) {
                summary += `   - Operative: ${operational}\n`;
            }
            const powerTypes = Object.entries(ctx.chargingStations.byPower)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3);
            if (powerTypes.length > 0) {
                summary += `   - Potenze: ${powerTypes.map(([p, c]) => `${p} (${c})`).join(', ')}\n`;
            }
            summary += '\n';
        } else {
            summary += `🔌 **Colonnine ricarica**: Nessuna nell'area\n\n`;
        }

        // Traffic
        if (ctx.traffic) {
            const levelEmoji = {
                low: '🟢',
                moderate: '🟡',
                high: '🟠',
                congestion: '🔴',
                unknown: '⚪'
            };
            summary += `🚦 **Traffico**: ${levelEmoji[ctx.traffic.level]} ${ctx.traffic.level}\n`;
            if (ctx.traffic.avgSpeed) {
                summary += `   - Velocità media: ${ctx.traffic.avgSpeed} km/h\n`;
            }
            summary += '\n';
        }

        // Weather
        if (ctx.weather) {
            summary += `🌤️ **Meteo attuale**: ${ctx.weather.current.temp}°C, ${ctx.weather.current.description}\n`;
            if (ctx.weather.current.precipitation > 0) {
                summary += `   - Precipitazioni: ${ctx.weather.current.precipitation}%\n`;
            }
            summary += '\n';
        }

        return summary.trim();
    });

    function updateContext(data: MapContextData) {
        mapContext.value = data;
        console.log('[MapContext] Context updated:', data);
    }

    function clearContext() {
        mapContext.value = null;
        console.log('[MapContext] Context cleared');
    }

    function getContextForAI(): string {
        if (!mapContext.value) return '';

        const ctx = mapContext.value;
        let aiContext = `CONTESTO MAPPA: L'utente ha selezionato un'area geografica di ${ctx.area.areaKm2.toFixed(2)} km² centrata su [${ctx.area.centerCoordinates[0].toFixed(4)}, ${ctx.area.centerCoordinates[1].toFixed(4)}].\n\n`;

        aiContext += `DATI NELL'AREA:\n`;
        aiContext += `- Colonnine di ricarica: ${ctx.chargingStations.total}\n`;

        if (ctx.chargingStations.total > 0) {
            const operational = ctx.chargingStations.byStatus['Operational'] || 0;
            aiContext += `  • Operative: ${operational}/${ctx.chargingStations.total}\n`;

            const powerList = Object.entries(ctx.chargingStations.byPower)
                .map(([power, count]) => `${power}: ${count}`)
                .join(', ');
            aiContext += `  • Per potenza: ${powerList}\n`;
        }

        if (ctx.traffic) {
            aiContext += `- Traffico: ${ctx.traffic.level}`;
            if (ctx.traffic.avgSpeed) {
                aiContext += ` (velocità media ${ctx.traffic.avgSpeed} km/h)`;
            }
            aiContext += '\n';
        }

        if (ctx.weather) {
            aiContext += `- Meteo: ${ctx.weather.current.temp}°C, ${ctx.weather.current.description}`;
            if (ctx.weather.current.precipitation > 0) {
                aiContext += `, precipitazioni ${ctx.weather.current.precipitation}%`;
            }
            aiContext += '\n';
        }

        aiContext += `\nRISPONDI alla domanda dell'utente basandoti ESCLUSIVAMENTE su questi dati reali dell'area selezionata.`;

        return aiContext;
    }

    return {
        mapContext: computed(() => mapContext.value),
        hasContext,
        contextSummary,
        updateContext,
        clearContext,
        getContextForAI,
    };
}
